/**
 * Sales Return Service
 * Thin typed wrapper around /api/v1/sales-returns, mirroring the shape of
 * customerService.ts / invoiceService.ts. apiClient auto-unwraps
 * response.data.data, so every method below returns the inner payload
 * object directly (e.g. { returns, count } or { salesReturn }).
 */
import { api } from './apiClient';

export type ReturnType =
  | 'DAMAGED'
  | 'WRONG_ITEM'
  | 'EXCESS'
  | 'QUALITY_ISSUE'
  | 'CUSTOMER_REJECTION'
  | 'OTHER';

export type ReturnStatus =
  | 'REQUESTED'
  | 'APPROVED'
  | 'RECEIVED'
  | 'INSPECTED'
  | 'ACCEPTED'
  | 'CREDIT_NOTE_ISSUED'
  | 'CLOSED'
  | 'REJECTED'
  | 'CANCELLED';

export type ItemCondition = 'GOOD' | 'DAMAGED' | 'SCRAP';

export interface SalesReturnRow {
  id: number;
  return_number: string;
  return_date: string;
  customer_id: number | string;
  customer_name?: string | null;
  invoice_id: number | string;
  invoice_number?: string | null;
  invoice_total_amount?: number | string | null;
  return_type: ReturnType;
  reason?: string | null;
  status: ReturnStatus;
  warehouse_id?: number | string | null;
  subtotal: number | string;
  cgst_amount: number | string;
  sgst_amount: number | string;
  igst_amount: number | string;
  total_amount: number | string;
  credit_note_id?: number | string | null;
  credit_note_number?: string | null;
  restock: boolean | number;
  created_by_name?: string | null;
  approved_by_name?: string | null;
}

export interface SalesReturnItem {
  id: number;
  product_id: number | string | null;
  product_name?: string | null;
  product_code?: string | null;
  product_uom?: string | null;
  sales_invoice_item_id?: number | string | null;
  quantity: number | string;
  unit_price: number | string;
  gst_rate: number | string;
  cgst_amount: number | string;
  sgst_amount: number | string;
  igst_amount: number | string;
  line_total: number | string;
  item_condition: ItemCondition;
  description?: string | null;
}

export interface SalesReturnDetail extends SalesReturnRow {
  items: SalesReturnItem[];
}

export interface SalesReturnStatusSummary {
  count: number;
  value: number;
}

export interface SalesReturnSummary {
  byStatus: Record<string, SalesReturnStatusSummary>;
  totalCount: number;
  totalValue: number;
  pendingCount: number;
  pendingValue: number;
  acceptedCount: number;
  creditNoteIssuedCount: number;
}

export interface SalesReturnFilters {
  status?: string;
  customerId?: number | string;
  invoiceId?: number | string;
  fromDate?: string;
  toDate?: string;
  search?: string;
}

export interface CreateSalesReturnLine {
  productId?: number | string;
  salesInvoiceItemId?: number | string;
  quantity: number;
  itemCondition?: ItemCondition;
  description?: string;
}

export interface CreateSalesReturnPayload {
  invoiceId: number | string;
  deliveryId?: number | string;
  returnDate?: string;
  returnType?: ReturnType;
  reason?: string;
  warehouseId?: number | string;
  restock?: boolean;
  lines: CreateSalesReturnLine[];
}

export const salesReturnService = {
  list: (filters?: SalesReturnFilters): Promise<{ returns: SalesReturnRow[]; count: number }> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.customerId !== undefined && filters?.customerId !== null && filters.customerId !== '') {
      params.append('customerId', String(filters.customerId));
    }
    if (filters?.invoiceId !== undefined && filters?.invoiceId !== null && filters.invoiceId !== '') {
      params.append('invoiceId', String(filters.invoiceId));
    }
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);
    if (filters?.search) params.append('search', filters.search);

    const qs = params.toString();
    return api.get<{ returns: SalesReturnRow[]; count: number }>(`/sales-returns${qs ? `?${qs}` : ''}`);
  },

  getSummary: (): Promise<{ summary: SalesReturnSummary }> => api.get('/sales-returns/summary'),

  getById: (id: number | string): Promise<{ salesReturn: SalesReturnDetail }> => api.get(`/sales-returns/${id}`),

  create: (payload: CreateSalesReturnPayload): Promise<{ salesReturn: SalesReturnDetail }> =>
    api.post('/sales-returns', payload),

  updateStatus: (id: number | string, status: ReturnStatus): Promise<{ salesReturn: SalesReturnDetail }> =>
    api.put(`/sales-returns/${id}/status`, { status }),

  approve: (id: number | string): Promise<{ salesReturn: SalesReturnDetail }> =>
    api.post(`/sales-returns/${id}/approve`),

  accept: (id: number | string): Promise<{ salesReturn: SalesReturnDetail }> =>
    api.post(`/sales-returns/${id}/accept`),

  issueCreditNote: (id: number | string): Promise<{ salesReturn: SalesReturnDetail; creditNote?: any }> =>
    api.post(`/sales-returns/${id}/credit-note`),
};

export default salesReturnService;
